<template>
  <div class="dashboard-layout">
    <!-- Top navbar for small devices only-->
    <TopNavbar
      v-if="$vuetify.breakpoint.smAndDown"
      @drawer:openNav="handleSideNavOpen"
      @drawer:openHelp="handleHelpOpen"
      @drawer:close="closeDrawer"
      :isSideNavOpen="isDrawerOpen && isDrawerNavActive"
      :isHelpOpen="isDrawerOpen && isDrawerHelpActive"
      :hideHelp="!isBookingsPathActive"
    />

    <!--
      Navigation drawer for mobile and desktop.
      it's displayed fixed in the left side of the layout for desktop.
      For mobile it appears from the right side of the layout moving the content left.
    -->
    <nav
      data-test="drawer"
      :class="{
        drawer: true,
        'drawer--drawerOpen': isDrawerOpen,
        'drawer--info': isDrawerHelpActive,
      }"
      role="navigation"
    >
      <!-- On small devices help and nav are switched inside the drawer -->
      <template v-if="$vuetify.breakpoint.smAndDown">
        <SidebarHelp v-if="isDrawerHelpActive" />
        <SideNavbar v-if="isDrawerNavActive" @navItemClick="closeDrawer()" />
      </template>

      <SideNavbar v-else @navItemClick="closeDrawer()" />
    </nav>

    <!-- Main content only if the user confirmed its email -->
    <main
      data-test="main"
      :class="{
        main: true,
        'main--drawerOpen': isDrawerOpen,
      }"
    >
      <div
        data-test="main-overlay"
        class="main__overlay"
        @click="closeDrawer"
      ></div>

      <div
        v-if="isEmailPendingVerification"
        data-test="pending-email-verification"
      >
        <ValidateEmailCard />
      </div>

      <slot v-else></slot>
    </main>

    <!-- Help sidebar only for desktop and in bookings view for now -->
    <div
      v-if="shouldDisplaySidebar"
      data-test="sidebar"
      :class="{
        sidebar: true,
        'sidebar--collapsed': isSidebarHelpCollapsed,
      }"
    >
      <SidebarHelp collapsable v-model="isSidebarHelpCollapsed" />
    </div>

    <!--
      Setup password overlay that blocks dashboard interactions
      if the user's never set a password before
    -->
    <SetupPasswordDialog
      data-test="setup-password-dialog"
      :isOpen="shouldDisplaySetupPasswordDialog"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import SideNavbar from '@/components/molecules/dashboard/navigation/sideNavbar.vue'
import TopNavbar from '@/components/molecules/dashboard/navigation/topNavbar.vue'
import SidebarHelp from '@/components/molecules/dashboard/sidebar/sidebarHelp.vue'
import ValidateEmailCard from '@/components/molecules/dashboard/validateEmailCard.vue'
import SetupPasswordDialog from '@/components/organisms/dashboard/setupPasswordDialog/setupPasswordDialog.vue'
import { eventBus } from '@/main'

export default {
  name: 'DashboardLayout',

  data() {
    return {
      isDrawerOpen: false,
      isDrawerHelpActive: false,
      isDrawerNavActive: false,
      isSidebarHelpCollapsed: true,
    }
  },

  components: {
    TopNavbar,
    SideNavbar,
    SetupPasswordDialog,
    ValidateEmailCard,
    SidebarHelp,
  },

  methods: {
    ...mapActions({
      fetchDashboardInitialData: 'user/fetchDashboardInitialData',
    }),

    handleSideNavOpen() {
      this.isDrawerNavActive = true
      this.isDrawerHelpActive = false
      this.openDrawer()
    },

    handleHelpOpen() {
      this.isDrawerHelpActive = true
      this.isDrawerNavActive = false
      this.openDrawer()
    },

    closeDrawer() {
      this.isDrawerOpen = false
    },

    openDrawer() {
      this.isDrawerOpen = true
    },

    registerOpenHelpSidebarHandler() {
      if (this.$vuetify.breakpoint.smAndDown) {
        this.handleHelpOpen()
        return
      }

      this.isSidebarHelpCollapsed = false
    },

    registerOpenHelpSidebarListener() {
      eventBus.$on('openHelpSidebar', this.registerOpenHelpSidebarHandler)
    },
  },

  computed: {
    ...mapGetters({
      shouldDisplaySetupPasswordDialog: 'user/shouldDisplaySetupPasswordDialog',
      isEmailPendingVerification: 'user/isEmailPendingVerification',
    }),

    isBookingsPathActive() {
      return this.$route.path.includes('meine-buchungen')
    },

    shouldDisplaySidebar() {
      return this.isBookingsPathActive && this.$vuetify.breakpoint.mdAndUp
    },
  },

  created() {
    this.fetchDashboardInitialData()
    this.registerOpenHelpSidebarListener()
  },
}
</script>
